import clsx from 'clsx';
import { useId, useRef, useState } from 'react';
import Btn from '../actions/Btn';
import { IconUpload } from '../icons';

export interface DragDropFileUploadProps {
    onSelect: (file: File) => void;
    /** Selected file name shown in the dropzone when set. */
    fileName?: string | null;
    accept?: string;
    disabled?: boolean;
    isUploading?: boolean;
    /** Helper text under the browse control (e.g. accepted types). */
    hint?: string;
    dropLabel?: string;
    browseLabel?: string;
    replaceLabel?: string;
    uploadingLabel?: string;
    className?: string;
    inputId?: string;
    /** Associates the control with an external visible label (`htmlFor` / `aria-labelledby`). */
    labelId?: string;
    inputAriaLabel?: string;
    hasError?: boolean;
}

/**
 * Full-width drag-and-drop file picker with a secondary outlined browse button.
 * Similar control pattern to `ImageUpload`, but for arbitrary file types.
 */
export function DragDropFileUpload({
    onSelect,
    fileName = null,
    accept,
    disabled = false,
    isUploading = false,
    hint,
    dropLabel = 'Drop file here or',
    browseLabel = 'Browse',
    replaceLabel = 'Replace file',
    uploadingLabel = 'Uploading…',
    className,
    inputId,
    labelId,
    inputAriaLabel = 'Choose file',
    hasError = false,
}: DragDropFileUploadProps) {
    const generatedInputId = useId();
    const resolvedInputId = inputId || generatedInputId;
    const statusId = `${resolvedInputId}-status`;
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    const hasFile = Boolean(fileName);
    const isControlDisabled = disabled || isUploading;
    const describedBy = isUploading ? statusId : undefined;
    const browseText = isUploading ? uploadingLabel : hasFile ? replaceLabel : browseLabel;

    const openFilePicker = () => {
        if (isControlDisabled) return;
        inputRef.current?.click();
    };

    const applyFileList = (list: FileList | null | undefined) => {
        const file = list?.[0];
        if (file) onSelect(file);
    };

    return (
        <div className={clsx('drag-drop-file-upload', className)}>
            <div
                className={clsx(
                    'drag-drop-file-upload-zone',
                    isDragging && 'is-dragging',
                    hasFile && 'has-file',
                    hasError && 'has-error',
                    isControlDisabled && 'is-disabled',
                )}
                onDragEnter={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!isControlDisabled) setIsDragging(true);
                }}
                onDragOver={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                }}
                onDragLeave={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setIsDragging(false);
                }}
                onDrop={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setIsDragging(false);
                    if (isControlDisabled) return;
                    applyFileList(event.dataTransfer.files);
                }}
            >
                <IconUpload iconClasses="drag-drop-file-upload-icon" />
                <div className="drag-drop-file-upload-prompt">
                    {hasFile ? (
                        <span className="drag-drop-file-upload-title">{fileName}</span>
                    ) : (
                        <span className="drag-drop-file-upload-title">{dropLabel}</span>
                    )}
                    <Btn
                        variant="secondary-outlined"
                        buttonSize="md"
                        buttonClasses="drag-drop-file-upload-browse"
                        buttonText={browseText}
                        buttonDisabled={isControlDisabled}
                        buttonOnClick={openFilePicker}
                        ariaLabel={inputAriaLabel}
                        buttonAriaProps={{
                            'aria-controls': resolvedInputId,
                            ...(describedBy ? { 'aria-describedby': describedBy } : {}),
                        }}
                    />
                </div>
                {hint && !hasFile ? <p className="drag-drop-file-upload-hint">{hint}</p> : null}
            </div>
            <input
                ref={inputRef}
                id={resolvedInputId}
                type="file"
                accept={accept}
                className="drag-drop-file-upload-input"
                disabled={isControlDisabled}
                aria-label={inputAriaLabel}
                tabIndex={-1}
                {...(labelId ? { 'aria-labelledby': labelId } : {})}
                onChange={(event) => {
                    applyFileList(event.target.files);
                    event.target.value = '';
                }}
            />
            {isUploading ? (
                <span
                    id={statusId}
                    className="drag-drop-file-upload-status"
                    role="status"
                    aria-live="polite"
                >
                    {uploadingLabel}
                </span>
            ) : null}
        </div>
    );
}

export default DragDropFileUpload;
